/**
 * Shared layout for the 5 entity detail pages (P2.7.e).
 *
 * One page composition:
 *   1. Header: kind label + entity name + code (after entity load)
 *   2. Chip strip (always-null in v0.2.0; backlog #435 lands the
 *      per-entity chip aggregator. The chip section renders "n/a"
 *      placeholders so the layout shape is stable when chips land.)
 *   3. Open findings panel via useFindings
 *
 * Routes (IBSDetail.tsx, ServiceDetail.tsx, etc) are thin wrappers
 * that read the `:id` URL param, narrow the kind, and pass it into
 * this layout. The chips prop exists so the layout can render real
 * chip values once #435 lands without changing the page wrappers.
 */
import { formatDateTime } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FindingLink } from "shared/components/FindingLink";

import { useEntityDetail, useFindings, useResilienceSnapshot } from "twin/api/hooks";
import { useDownloadEntityExport } from "twin/api/useDownloadEntityExport";
import { EntityChipStrip } from "twin/components/EntityChipStrip";
import { ChainDiffPanel } from "twin/entity/ChainDiffPanel";
import { hasResilienceChain } from "twin/entity/derivedChains";
import { ObligationsPanel } from "twin/entity/ObligationsPanel";
import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import type { MarkName } from "shared/icons/marks.generated";
import type {
  CascadeChips,
  EntityKind,
  Finding,
  ResilienceSnapshot,
  Severity,
} from "shared/api/types";
import { entityDetailPath } from "shared/routes/entityRoutes";

// P285.1: the entity's identity mark, by kind.
const KIND_MARK: Record<EntityKind, MarkName> = {
  ibs_service: "ibs",
  service: "constellation",
  capability: "capability-map",
  vendor: "blast-radius",
  configuration_item: "telemetry",
};

const SEVERITY_PILL_CLASSES: Record<Severity, string> = {
  critical: "bg-red-100 text-red-900 border-red-200",
  high: "bg-orange-100 text-orange-900 border-orange-200",
  medium: "bg-amber-100 text-amber-900 border-amber-200",
  low: "bg-slate-100 text-slate-800 border-slate-200",
};

function FindingRow({ finding }: { finding: Finding }) {
  return (
    <li
      data-testid="entity-finding"
      className="rounded-md border border-slate-200 bg-white p-3 space-y-1"
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_PILL_CLASSES[finding.severity]}`}
        >
          {finding.severity}
        </span>
        <FindingLink
          id={finding.id}
          title={finding.title}
          className="font-medium text-slate-900 break-words"
        />
      </div>
      <p className="text-xs text-slate-500 tabular-nums">
        {formatDateTime(finding.raised_at, i18n.language)}
      </p>
    </li>
  );
}

export interface EntityDetailLayoutProps {
  kind: EntityKind;
  /** Route param. Undefined while react-router resolves; the layout
   *  renders a "no id" placeholder in that case. */
  entityId: string | undefined;
  /** Chip values to render. Pages compute these from their kind-
   *  specific chain reads OR pass null when the kind has no chip
   *  data (e.g. configuration_item routes findings via linked
   *  service; no per-CI chain reads). */
  chips: CascadeChips;
  /** P275: an optional entity-lifecycle hub, rendered under the header.
   *  IBSDetail fills it with the IBS story hub; other wrappers pass
   *  nothing -> the shape is reusable for Service/Vendor later. */
  story?: ReactNode;
  /**
   * Round 10 C4 -- what to say when nothing is raised DIRECTLY on this
   * entity, for a page that knows why that is not the whole answer.
   */
  findingsEmpty?: ReactNode;
}

export function EntityDetailLayout({
  kind,
  entityId,
  chips,
  story,
  findingsEmpty,
}: EntityDetailLayoutProps) {
  const { t } = useTranslation();
  const entity = useEntityDetail(kind, entityId);
  // P272.4: read the LIVE resilience-state chain (the same one the cascade
  // reads) so the resilience chip matches the cascade instead of the old
  // hardcoded "n/a". Falls back to the page-passed chip on a 404 (never
  // snapshotted). The other three chips remain page-supplied (backlog #435).
  //
  // Round 9 E9 -- and not for a vendor, which the deriver has never
  // sealed and never will: a vendor is a propagation SOURCE in that
  // model, not a subject of it. Asking returned a correct 404 that the
  // page then had to absorb.
  const resilience = useResilienceSnapshot(kind, entityId, hasResilienceChain(kind));
  const liveChips: CascadeChips = {
    ...chips,
    resilience: resilience.data?.state ?? chips.resilience,
  };
  const findings = useFindings({
    entity_kind: kind,
    entity_id: entityId ?? "",
    status: "open",
    severities: ["critical", "high", "medium", "low"],
  });

  if (!entityId) {
    return (
      <p data-testid="entity-no-id" className="text-sm text-red-700">
        {t("screens.entityDetailLayout.missingEntityIdInUrl")}
      </p>
    );
  }

  if (entity.isLoading) {
    return (
      <p data-testid="entity-loading" className="text-sm text-slate-500">
        {t("modules.twin.entity.loading")}
      </p>
    );
  }

  if (entity.isError) {
    const is404 = entity.error.status === 404;
    return (
      <article className="max-w-3xl">
        <p
          data-testid={is404 ? "entity-not-found" : "entity-error"}
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-4"
        >
          <strong className="block font-semibold mb-1">
            {t(
              is404
                ? "modules.twin.entity.notFoundTitle"
                : "modules.twin.entity.errorTitle",
            )}
          </strong>
          {is404 ? t("modules.twin.entity.notFoundBody") : entity.error.detail}
        </p>
        <p className="mt-3 text-sm">
          <Link to="/twin/capmap" className="text-brand-brassDark hover:underline">
            {t("screens.entityDetailLayout.larrCapabilityMap")}
          </Link>
        </p>
      </article>
    );
  }

  const data = entity.data;
  if (!data) {
    return null;
  }

  return (
    <article
      className="max-w-4xl space-y-5"
      data-testid="entity-detail"
      data-entity-kind={data.kind}
      data-entity-id={data.id}
    >
      <header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">
              {t(
                data.kind === "ibs_service" && data.service_class === "internal"
                  ? "modules.twin.entity.kind.ibs_service_internal"
                  : `modules.twin.entity.kind.${data.kind}`,
              )}
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              <MarsadIcon
                name={KIND_MARK[data.kind]}
                size={20}
                className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
              />
              {data.name}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">{data.code}</p>
          </div>
          <EntityExportActions kind={data.kind} entityId={data.id} />
        </div>
        {/* Round 10 E1: the bank's own description of its own entity,
            which the page did not write and cannot assume the
            direction of. */}
        {data.description && (
          <p dir="auto" className="mt-2 text-sm text-slate-700 max-w-prose">
            {data.description}
          </p>
        )}
      </header>

      {story}

      {/* D7 -- a strip of four chips all reading "n/a" is a heading
          and a row of nothing, on a page a business reader is already
          scrolling. It appears when at least one chip has something to
          say. */}
      {(liveChips.resilience !== null ||
        liveChips.blast_ibs_count !== null ||
        liveChips.process_count !== null ||
        liveChips.obligation_count !== null) && (
        <section data-testid="entity-chips">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            {t("modules.twin.entity.chipsHeading")}
          </h2>
          {/* Round 10 C4: a chip this page never asked for is dropped
              rather than printed "n/a" beside a sentence that answers
              the same question. */}
          <EntityChipStrip chips={liveChips} omitNull />
        </section>
      )}

      <section data-testid="entity-findings">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          {t("modules.twin.entity.findingsHeading")}
        </h2>
        {findings.isLoading ? (
          <p data-testid="entity-findings-loading" className="text-sm text-slate-500">
            {t("modules.twin.entity.findingsLoading")}
          </p>
        ) : (findings.data ?? []).length === 0 ? (
          <p
            data-testid="entity-findings-empty"
            className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3"
          >
            {/* Round 10 C4 -- "No open findings on this entity" is true
                of THIS QUERY and false about the estate wherever the
                findings arrive on something the entity runs on. A page
                that knows better says so; the generic sentence stays
                for the pages that do not. */}
            {findingsEmpty ?? t("modules.twin.entity.findingsNone")}
          </p>
        ) : (
          <ul className="space-y-2">
            {(findings.data ?? []).map((f) => (
              <FindingRow key={f.id} finding={f} />
            ))}
          </ul>
        )}
        {/* Round 10 F2 -- why the chip is not green when nothing here
            is open. Eighth round of RESILIENCE reading amber beside
            "No open findings on this entity" with no explanation, and
            the answer was always in the sealed snapshot: the state is
            inherited from a degraded thing upstream, or it comes from
            live telemetry rather than from a finding. Neither fact
            reached the page. */}
        {/* ROUND 15 H1 -- and when findings ARE listed, the chip still
            says the two are one fact, rather than sitting bare above
            them, thirteenth round. */}
        <ResilienceWhy
          snapshot={resilience.data}
          findingsListed={(findings.data ?? []).length}
        />
      </section>

      <ObligationsPanel kind={kind} entityId={entityId} />

      <ChainDiffPanel entityKind={kind} entityId={entityId} />
    </article>
  );
}

// P62.2 (#481): per-entity export buttons. Admin-only; rendered in
// the page header alongside the entity title.
function EntityExportActions({
  kind,
  entityId,
}: {
  kind: EntityKind;
  entityId: string;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const download = useDownloadEntityExport();
  if (!me.data?.is_admin) return null;

  const onExport = (format: "pdf" | "json") => {
    download.mutate({ kind, entityId, format });
  };

  return (
    <div
      data-testid="entity-export-actions"
      className="flex flex-shrink-0 items-center gap-1"
    >
      <button
        type="button"
        data-testid="entity-export-pdf"
        disabled={download.isPending}
        onClick={() => onExport("pdf")}
        className="rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 disabled:opacity-50"
      >
        {download.isPending && download.variables?.format === "pdf"
          ? t("screens.entityDetailLayout.exporting")
          : t("screens.entityDetailLayout.exportPdf")}
      </button>
      <button
        type="button"
        data-testid="entity-export-json"
        disabled={download.isPending}
        onClick={() => onExport("json")}
        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 disabled:opacity-50"
      >
        {download.isPending && download.variables?.format === "json"
          ? t("screens.entityDetailLayout.exporting")
          : t("screens.entityDetailLayout.json2")}
      </button>
    </div>
  );
}

/**
 * The sentence that turns an unexplained colour into a fact.
 *
 * Only ever shown where the findings list is empty: where a finding IS
 * open the list above already says why, and repeating it would be
 * noise. Renders nothing for a green state, a missing snapshot, or a
 * state driven by findings -- an explanation nobody needs is the same
 * clutter this page has spent three rounds removing.
 */
function ResilienceWhy({
  snapshot,
  findingsListed = 0,
}: {
  snapshot: ResilienceSnapshot | undefined;
  findingsListed?: number;
}) {
  const { t, i18n } = useTranslation();
  if (!snapshot || snapshot.state === "green") return null;
  const reasons = snapshot.reasons ?? [];
  const dependency = reasons.find((r) => r.kind === "dependency");
  const telemetry = reasons.find((r) => r.kind === "telemetry");

  if (dependency) {
    const upstreamKind = String(dependency.upstream_kind ?? "");
    const upstreamId = String(dependency.upstream_id ?? "");
    return (
      <UpstreamReason
        state={snapshot.state}
        upstreamKind={upstreamKind}
        upstreamId={upstreamId}
        upstreamState={String(dependency.upstream_state ?? "")}
      />
    );
  }
  if (telemetry) {
    return (
      <p data-testid="entity-resilience-why" className="mt-2 text-sm text-slate-600">
        {t("modules.twin.entity.resilienceWhy.telemetry", {
          status: String(telemetry.status ?? ""),
        })}
      </p>
    );
  }

  /*
   * Round 11 F1 -- ninth round of RESILIENCE reading amber beside "No
   * open findings on this entity" with no explanation, and round 10's
   * fix is why it survived: it explained the two reasons that arrive
   * with a `kind`, and said nothing at all for the commonest one,
   * which arrives with a `finding_id` and no kind.
   *
   * That case is the confusing one, not an edge: the seal was taken
   * from open findings, and the list beneath it is empty because those
   * findings landed on a technology asset this service runs on and
   * were rerouted (P216), or because they were closed after the seal.
   * Either way the chip is not lying and the page was not saying why.
   *
   * The last branch reports what it can support and no more -- when
   * the snapshot was sealed -- rather than asserting a dependency
   * nobody recorded. A page that guesses the cause is worse than one
   * that says the state is older than the list.
   */
  const fromFindings = reasons.filter((r) => r.finding_id !== undefined);
  if (fromFindings.length > 0 && findingsListed > 0) {
    return (
      <p data-testid="entity-resilience-why" className="mt-2 text-sm text-slate-600">
        {t("modules.twin.entity.resilienceWhy.ownFindings", {
          state: snapshot.state,
          count: findingsListed,
        })}
      </p>
    );
  }
  if (fromFindings.length > 0) {
    return (
      <div data-testid="entity-resilience-why" className="mt-2 text-sm text-slate-600">
        <p>
          {t("modules.twin.entity.resilienceWhy.sealedOnFindings", {
            state: snapshot.state,
            count: fromFindings.length,
          })}
        </p>
        <ul className="mt-1 list-disc space-y-0.5 ps-5 text-xs">
          {fromFindings.map((r, i) => (
            <li key={String(r.finding_id ?? i)} dir="auto">
              {String(r.title ?? r.external_ref ?? "")}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <p data-testid="entity-resilience-why" className="mt-2 text-sm text-slate-600">
      {t("modules.twin.entity.resilienceWhy.sealedEarlier", {
        state: snapshot.state,
        when: formatDateTime(snapshot.captured_at, i18n.language),
      })}
    </p>
  );
}

/**
 * Names the upstream rather than printing its uuid, and links to it:
 * the reader's next question is always "which one", and the answer is
 * a page this product already has.
 */
function UpstreamReason({
  state,
  upstreamKind,
  upstreamId,
  upstreamState,
}: {
  state: string;
  upstreamKind: string;
  upstreamId: string;
  upstreamState: string;
}) {
  const { t } = useTranslation();
  const upstream = useEntityDetail(upstreamKind as EntityKind, upstreamId);
  const href = entityDetailPath(upstreamKind, upstreamId);
  const name = upstream.data?.name;
  return (
    <p data-testid="entity-resilience-why" className="mt-2 text-sm text-slate-600">
      {t("modules.twin.entity.resilienceWhy.dependency", {
        state,
        upstreamState,
      })}{" "}
      {name ? (
        href ? (
          <Link to={href} className="text-brand-brassDark hover:underline">
            {name}
          </Link>
        ) : (
          <span className="font-medium">{name}</span>
        )
      ) : (
        <span className="text-slate-400">
          {t("modules.twin.entity.resilienceWhy.unnamedUpstream")}
        </span>
      )}
      {"."}
    </p>
  );
}
